import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Sparkles, X, Upload, Wand2, Download, RefreshCw, Image as ImageIcon, Check } from "lucide-react";
import type { DesignStyle, DesignStyleItem, Design } from "@erc/shared";
import { apiClient } from "../../lib/api";
import env from "../../env";
import { useDesignModel, modelLabel } from "../../lib/useDesignModel";

const abs = (u?: string | null) =>
  !u ? "" : u.startsWith("http") || u.startsWith("data:") || u.startsWith("blob:") ? u : `${env.API_URL}${u}`;

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  analyzing: { cls: "bg-amber-100 text-amber-700", label: "Analiz ediliyor…" },
  ready: { cls: "bg-emerald-100 text-emerald-700", label: "Hazır" },
  failed: { cls: "bg-red-100 text-red-600", label: "Başarısız" },
};

export function DesignStylesTab() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [openStyleId, setOpenStyleId] = useState<string | null>(null);

  const { data: styles = [] } = useQuery({
    queryKey: ["design-styles"],
    queryFn: () => apiClient.listDesignStyles(),
    refetchInterval: (q) => {
      const data = q.state.data as DesignStyle[] | undefined;
      return data?.some((s) => s.status === "analyzing") ? 3000 : false;
    },
  });

  const delM = useMutation({
    mutationFn: (id: string) => apiClient.deleteDesignStyle(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["design-styles"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasarım Stilleri</h1>
          <p className="text-sm text-gray-500 mt-1">Bir stili öğret (bir veya birden fazla örnek) → o stilde kelime ya da referans görselle yeni tasarımlar üret.</p>
        </div>
        <button onClick={() => setAdding(true)} className="flex flex-shrink-0 items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
          <Plus className="w-4 h-4" /> Stil Ekle
        </button>
      </div>

      {styles.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl py-16 text-center text-sm text-gray-400">
          Henüz stil yok. <b>Stil Ekle</b> ile bir veya birden fazla örnek tasarım yükleyip stili öğret; sistem stili analiz eder ve sonra o stille yeni tasarımlar üretirsin.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {styles.map((s) => (
            <StyleCard key={s.id} style={s} onOpen={() => setOpenStyleId(s.id)} onDelete={() => { if (confirm(`"${s.name}" stilini sil?`)) delM.mutate(s.id); }} />
          ))}
        </div>
      )}

      {adding && <AddStyleModal onClose={() => setAdding(false)} onCreated={() => { setAdding(false); qc.invalidateQueries({ queryKey: ["design-styles"] }); }} />}
      {openStyleId && <StyleDetailModal styleId={openStyleId} onClose={() => setOpenStyleId(null)} onChanged={() => qc.invalidateQueries({ queryKey: ["design-styles"] })} />}
    </div>
  );
}

function StyleCard({ style, onOpen, onDelete }: { style: DesignStyle; onOpen: () => void; onDelete: () => void }) {
  const badge = STATUS_BADGE[style.status] ?? STATUS_BADGE.ready;
  const count = style._count?.items ?? 0;
  return (
    <div className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white hover:shadow-md transition-shadow">
      <button onClick={onOpen} className="block w-full">
        <div className="aspect-square w-full bg-gray-50">
          {style.thumbnailUrl ? (
            <img src={abs(style.thumbnailUrl)} alt={style.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-gray-300"><ImageIcon className="h-8 w-8" /></div>
          )}
        </div>
        <div className="p-2 text-left">
          <p className="truncate text-xs font-medium text-gray-800" title={style.name}>{style.name}</p>
          <div className="mt-1 flex items-center gap-1">
            <span className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${badge.cls}`}>
              {style.status === "analyzing" && <Loader2 className="mr-0.5 inline h-2.5 w-2.5 animate-spin" />}{badge.label}
            </span>
            {style.referenceImageUrls.length > 1 && <span className="text-[9px] text-gray-400">{style.referenceImageUrls.length} örnek</span>}
            {count > 0 && <span className="ml-auto text-[9px] text-indigo-500">{count} tasarım</span>}
          </div>
        </div>
      </button>
      <button onClick={onDelete} title="Sil" className="absolute right-1 top-1 rounded bg-white/80 p-1 text-gray-400 opacity-0 hover:text-red-500 group-hover:opacity-100">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Stil Ekle Modalı ──────────────────────────────────────
function AddStyleModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [files, setFiles] = useState<File[]>([]);      // yuklenen dosyalar
  const [poolUrls, setPoolUrls] = useState<string[]>([]); // Havuzdan secilen http URL'ler
  const [poolOpen, setPoolOpen] = useState(false);

  // Yuklenen dosyalar icin blob onizleme URL'leri — her zaman render olur (data URL/CSP'den bagimsiz)
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => { previews.forEach((u) => URL.revokeObjectURL(u)); }, [previews]);

  const total = files.length + poolUrls.length;

  const createM = useMutation({
    mutationFn: async () => {
      const dataUrls = await Promise.all(files.map(fileToDataUrl));
      return apiClient.createDesignStyle({ name: name.trim(), images: [...dataUrls, ...poolUrls] });
    },
    onSuccess: onCreated,
  });

  function onFiles(list: FileList | null) {
    if (!list) return;
    const arr = Array.from(list).filter((f) => f.type.startsWith("image/"));
    setFiles((prev) => [...prev, ...arr].slice(0, 8));
  }

  const canCreate = !!name.trim() && total > 0 && !createM.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><Sparkles className="h-5 w-5 text-indigo-600" /> Stil Ekle</h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Stil adı</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="örn. Pembe Çizgi Çerçeve"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Örnek tasarımlar <span className="text-gray-400">(birden fazla = daha net stil, en fazla 8)</span></label>
            <div className="grid grid-cols-4 gap-2">
              {files.map((_, i) => (
                <div key={`f${i}`} className="relative aspect-square overflow-hidden rounded-md border border-gray-200">
                  <img src={previews[i]} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => setFiles((p) => p.filter((_, x) => x !== i))} className="absolute right-0.5 top-0.5 rounded bg-white/80 p-0.5 text-gray-500 hover:text-red-500">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {poolUrls.map((u) => (
                <div key={`p${u}`} className="relative aspect-square overflow-hidden rounded-md border border-gray-200">
                  <img src={abs(u)} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => setPoolUrls((p) => p.filter((x) => x !== u))} className="absolute right-0.5 top-0.5 rounded bg-white/80 p-0.5 text-gray-500 hover:text-red-500">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {total < 8 && (
                <label className="flex aspect-square cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-gray-300 text-gray-400 hover:bg-gray-50">
                  <Upload className="h-4 w-4" />
                  <span className="mt-0.5 text-[9px]">Yükle</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
                </label>
              )}
            </div>
            <button onClick={() => setPoolOpen((v) => !v)} className="mt-2 text-[11px] font-medium text-indigo-600 hover:text-indigo-700">
              {poolOpen ? "Havuzdan seçmeyi kapat" : "veya Tasarım Havuzundan seç →"}
            </button>
            {poolOpen && <PoolPicker selected={poolUrls} onToggle={(url) => setPoolUrls((p) => p.includes(url) ? p.filter((x) => x !== url) : (total < 8 ? [...p, url] : p))} />}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t p-3">
          {createM.isError && <span className="mr-auto text-xs text-red-500">{(createM.error as Error)?.message ?? "Eklenemedi"}</span>}
          {!createM.isError && !name.trim() && total > 0 && <span className="mr-auto text-xs text-amber-600">Bir stil adı girin</span>}
          {!createM.isError && total === 0 && <span className="mr-auto text-xs text-amber-600">En az bir örnek görsel ekleyin</span>}
          <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
          <button onClick={() => createM.mutate()} disabled={!canCreate}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            {createM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Ekle ve Analiz Et
          </button>
        </div>
      </div>
    </div>
  );
}

// Tasarım Havuzundan görsel seçici
function PoolPicker({ selected, onToggle }: { selected: string[]; onToggle: (url: string) => void }) {
  const { data } = useQuery({
    queryKey: ["design-styles-pool-picker"],
    queryFn: () => apiClient.listDesigns({ limit: 40, sort: "newest" }),
  });
  const designs = (data?.data ?? []) as Design[];
  return (
    <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200 p-2">
      {designs.length === 0 ? (
        <p className="py-4 text-center text-[11px] text-gray-400">Havuzda tasarım yok.</p>
      ) : (
        <div className="grid grid-cols-5 gap-1.5">
          {designs.map((d) => {
            const url = d.editedUrl || d.originalUrl;
            const on = selected.includes(url);
            return (
              <button key={d.id} onClick={() => onToggle(url)} className={`relative aspect-square overflow-hidden rounded border-2 ${on ? "border-indigo-500" : "border-transparent"}`}>
                <img src={abs(url)} alt="" loading="lazy" className="h-full w-full object-cover" />
                {on && <span className="absolute right-0.5 top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-indigo-600 text-white"><Check className="h-2.5 w-2.5" /></span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Stil Detay + Üretim Modalı ────────────────────────────
function StyleDetailModal({ styleId, onClose, onChanged }: { styleId: string; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const { models, model, setModel } = useDesignModel("erc:model:design-style");
  const [subjectPrompt, setSubjectPrompt] = useState("");
  const [subjectImage, setSubjectImage] = useState<string | null>(null);

  const { data: style } = useQuery({
    queryKey: ["design-style", styleId],
    queryFn: () => apiClient.getDesignStyle(styleId),
    refetchInterval: (q) => {
      const s = q.state.data as DesignStyle | undefined;
      if (!s) return false;
      return s.status === "analyzing" || (s.items ?? []).some((i) => i.status === "generating") ? 3000 : false;
    },
  });

  const genM = useMutation({
    mutationFn: () => apiClient.generateStyledDesign(styleId, { subjectPrompt: subjectPrompt.trim() || undefined, subjectImage: subjectImage || undefined, model }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["design-style", styleId] }); onChanged(); },
  });

  const reM = useMutation({
    mutationFn: () => apiClient.reanalyzeDesignStyle(styleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["design-style", styleId] }),
  });

  const delItemM = useMutation({
    mutationFn: (itemId: string) => apiClient.deleteStyleItem(styleId, itemId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["design-style", styleId] }); onChanged(); },
  });

  async function onSubjectFile(files: FileList | null) {
    if (!files?.[0]) return;
    setSubjectImage(await fileToDataUrl(files[0]));
  }

  const sa = style?.styleAnalysis;
  const ready = style?.status === "ready";
  const canGen = ready && model && (subjectPrompt.trim() || subjectImage) && !genM.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Sparkles className="h-5 w-5 text-indigo-600" /> {style?.name ?? "Stil"}
            {style && <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${(STATUS_BADGE[style.status] ?? STATUS_BADGE.ready).cls}`}>{(STATUS_BADGE[style.status] ?? STATUS_BADGE.ready).label}</span>}
          </h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        {!style ? (
          <div className="flex flex-1 items-center justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 md:flex-row">
            {/* Sol: referanslar + analiz */}
            <div className="w-full space-y-3 md:w-72 md:flex-shrink-0">
              <div>
                <p className="mb-1 text-xs font-medium text-gray-500">Örnek tasarımlar ({style.referenceImageUrls.length})</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {style.referenceImageUrls.map((u, i) => (
                    <img key={i} src={abs(u)} alt="" className="aspect-square w-full rounded-md border border-gray-200 object-cover" />
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-2.5 text-[11px] text-gray-700">
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-semibold text-gray-600">Stil analizi</span>
                  <button onClick={() => reM.mutate()} disabled={reM.isPending || style.status === "analyzing"} title="Yeniden analiz et"
                    className="flex items-center gap-1 text-[10px] text-indigo-600 hover:text-indigo-700 disabled:opacity-50">
                    <RefreshCw className={`h-3 w-3 ${reM.isPending ? "animate-spin" : ""}`} /> Yeniden
                  </button>
                </div>
                {style.status === "analyzing" ? (
                  <p className="flex items-center gap-1 text-gray-400"><Loader2 className="h-3 w-3 animate-spin" /> Analiz ediliyor…</p>
                ) : style.status === "failed" ? (
                  <p className="text-red-500">{style.error || "Analiz başarısız"}</p>
                ) : sa ? (
                  <div className="space-y-1">
                    {sa.summary && <p>{sa.summary}</p>}
                    {sa.background && <p><b>Arka plan:</b> {sa.background}</p>}
                    {sa.layout && <p><b>Yerleşim:</b> {sa.layout}</p>}
                    {sa.technique && <p><b>Teknik:</b> {sa.technique}</p>}
                    {sa.framing && <p><b>Çerçeve:</b> {sa.framing}</p>}
                    {sa.palette && sa.palette.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 pt-0.5"><b>Palet:</b>
                        {sa.palette.slice(0, 8).map((c, i) => <span key={i} title={c} className="inline-block h-4 w-4 rounded border border-gray-300" style={{ background: c }} />)}
                      </div>
                    )}
                  </div>
                ) : <p className="text-gray-400">Analiz yok.</p>}
              </div>
            </div>

            {/* Sag: uret + galeri */}
            <div className="flex-1 space-y-3">
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
                <p className="mb-2 text-xs font-semibold text-indigo-700">Bu stille yeni tasarım üret</p>
                <textarea value={subjectPrompt} onChange={(e) => setSubjectPrompt(e.target.value)} rows={2}
                  placeholder="Konu / kelime (örn. Halloween ghost, 'ain't no ghost like the holy ghost')"
                  className="w-full resize-y rounded-lg border border-gray-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {subjectImage ? (
                    <div className="relative h-12 w-12 overflow-hidden rounded border border-gray-200">
                      <img src={subjectImage} alt="" className="h-full w-full object-cover" />
                      <button onClick={() => setSubjectImage(null)} className="absolute right-0 top-0 rounded bg-white/80 p-0.5 text-gray-500 hover:text-red-500"><X className="h-3 w-3" /></button>
                    </div>
                  ) : (
                    <label className="flex cursor-pointer items-center gap-1 rounded-lg border border-dashed border-gray-300 px-2.5 py-2 text-[11px] text-gray-500 hover:bg-gray-50">
                      <Upload className="h-3.5 w-3.5" /> Konu görseli (ops.)
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { onSubjectFile(e.target.files); e.target.value = ""; }} />
                    </label>
                  )}
                  <select value={model} onChange={(e) => setModel(e.target.value)}
                    className="rounded-lg border border-gray-200 px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    {models.length === 0 && <option value="">Model…</option>}
                    {models.map((m) => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
                  </select>
                  <button onClick={() => genM.mutate()} disabled={!canGen}
                    className="ml-auto flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                    {genM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Üret
                  </button>
                </div>
                {!ready && <p className="mt-1.5 text-[11px] text-amber-600">Stil analizi tamamlanınca üretim aktif olur.</p>}
                {genM.isError && <p className="mt-1 text-xs text-red-500">{(genM.error as Error)?.message ?? "Hata"}</p>}
              </div>

              {/* Uretilen tasarimlar */}
              {(style.items ?? []).length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-xs text-gray-400">Henüz bu stille tasarım üretilmedi.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {(style.items ?? []).map((it) => <StyleItemCard key={it.id} item={it} onDelete={() => delItemM.mutate(it.id)} />)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StyleItemCard({ item, onDelete }: { item: DesignStyleItem; onDelete: () => void }) {
  return (
    <div className="group relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
      <div className="aspect-square w-full">
        {item.status === "completed" && item.imageUrl ? (
          <img src={abs(item.imageUrl)} alt="" className="h-full w-full object-contain" />
        ) : item.status === "failed" ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 p-2 text-center">
            <span className="text-xs font-medium text-red-500">Başarısız</span>
            <span className="text-[10px] text-gray-400">{item.error?.slice(0, 80)}</span>
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin" /><span className="text-[10px]">Üretiliyor…</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between border-t border-gray-100 bg-white px-2 py-1.5">
        <span className="truncate text-[10px] text-gray-500" title={item.subjectPrompt || "görsel konu"}>{item.subjectPrompt || "görsel konu"}</span>
        <div className="flex flex-shrink-0 items-center gap-1">
          {item.status === "completed" && item.imageUrl && (
            <a href={abs(item.imageUrl)} download target="_blank" rel="noopener noreferrer" title="İndir" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><Download className="h-3.5 w-3.5" /></a>
          )}
          <button onClick={onDelete} title="Sil" className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  );
}
